import React from 'react';
import {
	Box,
	Button,
	FormLabel,
	IconButton,
	Skeleton,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
import { useDispatch } from 'react-redux';
import { openDialog } from '@/features/dialog/dialogSlice';
import { useListInformedConsentsQuery } from '@/features/clients/clientsApi';
import { CONSENT_STATUS } from '@/features/informedConsent/helpers';
import { formatDate } from '@/utils/constants';
import {
	InformedConsentStatusChip,
	CONSENT_STATUS_CHIP_SX,
} from '@/components/InformedConsentStatusChip';
import { NoteAddOutlined } from '@mui/icons-material';
import { useFeatureFlagContext } from '@/context/featureFlagContext';

export const InformedConsentCard = ({ clientId }) => {
	const dispatch = useDispatch();

	const { getFeatureFlags } = useFeatureFlagContext();
	const informedConsentEnabled = Boolean(getFeatureFlags('informedConsent')?.active);

	const { data: records = [], isLoading } = useListInformedConsentsQuery(clientId, {
		skip: !clientId || !informedConsentEnabled,
	});

	const latest = records[0] ?? null;
	const status = latest?.status ?? CONSENT_STATUS.NONE;

	const openRecordDialog = () => {
		dispatch(openDialog('recordInformedConsent'));
	};

	const openHistoryDialog = () => {
		dispatch(openDialog('informedConsentHistory'));
	};

	if (!informedConsentEnabled) {
		return <></>;
	}

	return (
		<Stack>
			<Stack direction='row' justifyContent='space-between' alignItems='center'>
				<FormLabel>Informed consent</FormLabel>
				<Tooltip title='View history'>
					<IconButton color='primary' size='small' onClick={openHistoryDialog}>
						<ManageHistoryIcon sx={{ fontSize: '18px' }} />
					</IconButton>
				</Tooltip>
			</Stack>
			<Box
				sx={(theme) => ({
					border: `1px solid ${theme.palette.divider}`,
					borderRadius: 1,
					p: 1.5,
				})}
			>
				<Stack
					direction='row'
					justifyContent='space-between'
					alignItems='flex-end'
					spacing={1}
				>
					<Stack
						spacing={status === CONSENT_STATUS.NONE ? 0.5 : 1}
						alignItems='flex-start'
					>
						{isLoading ? (
							<Skeleton variant='text' width={160} />
						) : status === CONSENT_STATUS.NONE ? (
							<Typography variant='body2'>No informed consent recorded</Typography>
						) : (
							<>
								<InformedConsentStatusChip status={status} />
								<Typography
									variant='body2'
									sx={{
										color:
											status === CONSENT_STATUS.EXPIRED
												? CONSENT_STATUS_CHIP_SX[CONSENT_STATUS.EXPIRED]
														.color
												: 'text.secondary',
									}}
								>
									{formatDate(latest.dateReceived, true)}
								</Typography>
							</>
						)}
					</Stack>
					<Tooltip title='Record consent'>
						<IconButton color='primary' size='small' onClick={openRecordDialog}>
							<NoteAddOutlined sx={{ fontSize: '18px' }} />
						</IconButton>
					</Tooltip>
				</Stack>
			</Box>
		</Stack>
	);
};
